/**
 * Classify each shared wall corner of a kitchen for layout purposes.
 *
 * For every (wallA, wallB) pair sharing an endpoint, decide:
 *   - 'corner-cabinet' — both walls have free run at the corner end
 *     (after subtracting appliances + door/opening blockers) for an L
 *     the cabinet line sells: 36", or Venus's 33" (`lLength`). Place an
 *     L corner cabinet.
 *   - 'appliance-on-A' / 'appliance-on-B' — one wall has an appliance
 *     touching the corner end. The perpendicular wall reserves a
 *     buffer = appliance depth (no corner cabinet).
 *   - 'wallA-only' / 'wallB-only' — only one wall has free run at
 *     the corner end (the other has door/opening or insufficient
 *     space). The eligible wall's cabinets run to the corner; no
 *     buffer needed.
 *   - 'none' — neither wall has free run; no cabinetry meets at this
 *     corner.
 *
 * Convention: wallA is always the wall whose `bottomLeft` endpoint
 * is at the corner (so `wallAEnd` is implicitly 'left'). wallB's
 * end at the corner is reported in `wallBEnd` ('left' or 'right').
 *
 * Pure: takes data in, returns classifications.
 */

const { getAvailableWallSegments } = require('./wallSegments');
const { CORNER_CLAIM, legHoldsAtCorner } = require('./legRule');
const { getWallAxes, worldToWallParam } = require('./wallMath');
const { claimsCorner } = require('./cornerProximity');
const {
  INCHES_TO_METERS,
  MIN_CABINET_WIDTH,
  KITCHEN_CABINET_DIMENSIONS,
  APPLIANCE_FRONT_CLEARANCE,
  SINGLE_SIDED_CORNER_LENGTH,
} = require('./constants');
const {
  DEFAULT_CORNER_BLIND_SET,
  DEFAULT_CORNER_L_SET,
  legTakes,
  pickCornerLIn,
  shortestCornerBlindM,
} = require('./cornerBlind');

/**
 * The corner units the cabinet LINE sells, as `classifyCorner` asks about them:
 * `blindSet` and `lSet` from `cornerBlind.js`. With none, the default sets —
 * a 36" L and blind bases from 36" — which is every corner the generator
 * planned before a line could be named.
 */
const DEFAULT_CORNER_UNITS = Object.freeze({
  blindSet: DEFAULT_CORNER_BLIND_SET,
  lSet: DEFAULT_CORNER_L_SET,
});

const EPS = 0.01;
const { convexIntersection } = require('../geometry/convexOverlap');

/**
 * A corner within this many degrees of a right angle is SQUARE: the square
 * corner products (the L corner cabinet, the blind box, the empty box) are
 * planned there. Past it the corner is OBLIQUE — the legs stop on the angle
 * bisector and a kite fills the wedge — because every square product assumes
 * the two walls are orthogonal, and its arm drifts off the far wall by
 * 36" x sin(off-square): 3.1" at 5°, 9.2" at 15°.
 *
 * ONE number for every "square or oblique?" question: the generator's corner
 * classes, the shape graph (`segmentGraph`), the run engine (`corners`
 * `isObliqueCorner`), the scan-run join and the scan path's oblique mitre.
 * The generator and the engine once held 11.5° and 15°; a 104.7° corner fell
 * between them, the generator recorded a kite, and the applied run built a
 * square L that could not fit (prod, 2026-09-28).
 *
 * 5° (user ruling, 2026-09-28) sits just past the 4° within which Step 2.3
 * turns a wall with no evidence of a real skew to the exact axis, so a corner
 * the pipeline leaves further off square than this is meant to be.
 */
const SQUARE_CORNER_TOL_DEG = 5;

/** |cos| of the angle between the two legs, below which a corner is square. */
const PERPENDICULAR_COS_TOL = Math.sin((SQUARE_CORNER_TOL_DEG * Math.PI) / 180);

/** Is a corner whose legs meet with this cosine square (`SQUARE_CORNER_TOL_DEG`)? */
function isSquareCornerCos(cos) {
  return Math.abs(cos) < PERPENDICULAR_COS_TOL;
}

/**
 * The direction a wall's cabinet RUN extends from the corner, as a unit
 * vector. Derived from the wall's junction (`dirSign` × `wallDir`), so
 * it's correct for a run leaving from either wall end AND for a run
 * leaving a mid-span junction. Falls back to the legacy end-label
 * derivation when a junction isn't attached (defensive).
 */
function runDirOf(wall, junction, end) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const sign = junction
    ? junction.dirSign
    : end === 'right' || end === 'left'
      ? end === 'left'
        ? 1
        : -1
      : 1;
  return { x: sign * axes.wallDir.x, z: sign * axes.wallDir.z };
}

/**
 * Returns true when the two runs meeting at the corner are within the
 * perpendicular tolerance. Uses each wall's run direction (see
 * `runDirOf`) — for a shared wall-end corner this reduces to wallA's
 * `wallDir` and ±wallB's `wallDir`, exactly as before.
 */
function isPerpendicularCorner(corner) {
  const dA = runDirOf(corner.wallA, corner.aJunction, 'left');
  const dB = runDirOf(corner.wallB, corner.bJunction, corner.wallBEnd);
  if (!dA || !dB) return false;
  return isSquareCornerCos(dA.x * dB.x + dA.z * dB.z);
}

/**
 * Interior angle (radians) at the corner, measured through the room
 * interior. Returns null if the wall axes can't be resolved.
 *
 * In our convention `wallNormal = rotate(wallDir, +90° CCW)` and the
 * normal points into the room — so going CCW from wallA's run
 * direction reaches wallB's run direction through the interior. The CCW
 * angle in (0, 2π] is the interior angle.
 */
function interiorAngleAt(corner) {
  const dA = runDirOf(corner.wallA, corner.aJunction, 'left');
  const dB = runDirOf(corner.wallB, corner.bJunction, corner.wallBEnd);
  if (!dA || !dB) return null;
  const crossZ = dA.x * dB.z - dA.z * dB.x;
  const dot = dA.x * dB.x + dA.z * dB.z;
  let ang = Math.atan2(crossZ, dot);
  if (ang <= 0) ang += 2 * Math.PI;
  return ang;
}

/**
 * Distance along each wall at which a 24"-deep cabinet on each side
 * must stop so the two cabinets' inner-room corners just touch on the
 * angle bisector — no body overlap, fronts meet at a single point.
 *
 *   stop = depth / tan(angle / 2)
 *
 * For 90°: 24". For 120°: ~13.86". For 60°: ~41.57".
 *
 * Returns 0 (no reservation) for:
 *   - reflex / concave corners (≥ 180°): the room is concave at the
 *     joint, cabinets on each wall pass the corner without touching.
 *   - near-flat joints (> 175°): formula collapses to ~0 already.
 *   - very acute joints (< 5°): formula explodes; caller should
 *     handle these as "no corner reservation needed" since no real
 *     cabinet would fit.
 */
function nonRightAngleStopDistance(angleRad) {
  return runStopDistance(
    angleRad,
    KITCHEN_CABINET_DIMENSIONS.depth,
    KITCHEN_CABINET_DIMENSIONS.depth
  );
}

/**
 * The general form: how far along wall A a run `depthA` deep must start so it
 * clears a run `depthB` deep on the wall meeting it at interior angle θ.
 *
 *   s = (depthB + depthA x cos θ) / sin θ
 *
 * At 90 degrees this is just `depthB` — perpendicular runs pass each other and
 * only the other run's depth matters. As the corner closes, this run's own
 * depth starts to count too, because its far-inner corner swings across the
 * other wall: at 47 degrees a 24" cabinet needs 55" but a 30" fridge needs 61".
 * Sizing that reservation for a cabinet and then seating a fridge in it is what
 * put a fridge through a wall.
 *
 * `nonRightAngleStopDistance` is the equal-depth case (two 24" cabinet runs),
 * where the formula collapses to `depth / tan(θ/2)`.
 *
 * Returns 0 where no reservation applies — see the guards on the reflex,
 * near-flat and degenerate cases in the caller's docs above.
 */
/**
 * How far along a leg its cabinets must stop to clear a BODY standing on the
 * other leg — the case `runStopDistance` does not cover: a body that does not
 * itself stop at the bisector but reaches the vertex, like a fridge parked
 * against the corner on the other wall, 30" deep and 30" wide. In the vertex
 * frame (leg A along +x, the room above it, leg B leaving at the interior
 * angle θ) the body is the rectangle along ∈ [near, far], depth ∈ [0, depth]
 * on A, and the leg's cabinet strip is 0 ≤ into-B ≤ ownDepth; the stop is the
 * furthest point along B of the body's part inside that strip. An exact
 * convex clip, so the strip never overlaps the body by construction. At a
 * right angle with the body at the vertex this is the body's depth — the
 * `appliance-on-*` reach without its clearance; at an obtuse joint the body's
 * front corner swings past the bisector, which the equal-depth formula
 * ignored (08E54F9E's galley, 2026-09-13). Zero when nothing of the body
 * lies in the strip.
 *
 * @param {number} angleRad interior angle between the legs
 * @param {number} ownDepth the stopping leg's cabinet depth (metres)
 * @param {{near?: number, far: number, depth: number}} body on the other leg,
 *   distances from the vertex along it and its depth off it (metres)
 */
function legStopClearingBody(angleRad, ownDepth, body) {
  if (!(angleRad > 0) || angleRad >= Math.PI || !(ownDepth > 0)) return 0;
  const near = body?.near > 0 ? body.near : 0;
  const { far, depth } = body || {};
  if (!(far > near) || !(depth > 0)) return 0;
  const uB = { x: Math.cos(angleRad), z: Math.sin(angleRad) };
  const nB = { x: Math.sin(angleRad), z: -Math.cos(angleRad) };
  const rect = [
    { x: near, z: 0 },
    { x: far, z: 0 },
    { x: far, z: depth },
    { x: near, z: depth },
  ];
  const reach = far + depth + ownDepth + 1;
  const at = (along, into) => ({
    x: along * uB.x + into * nB.x,
    z: along * uB.z + into * nB.z,
  });
  const strip = [
    at(-reach, 0),
    at(reach, 0),
    at(reach, ownDepth),
    at(-reach, ownDepth),
  ];
  let stop = 0;
  for (const q of convexIntersection(rect, strip)) {
    stop = Math.max(stop, q.x * uB.x + q.z * uB.z);
  }
  return stop;
}

function runStopDistance(angleRad, depthA, depthB) {
  if (angleRad == null || angleRad <= 0) return 0;
  if (angleRad >= Math.PI) return 0;
  if (angleRad > Math.PI - 5 * (Math.PI / 180)) return 0;
  if (angleRad < 5 * (Math.PI / 180)) return 0;
  const sin = Math.sin(angleRad);
  if (!Number.isFinite(sin) || sin <= 0) return 0;
  return Math.max(0, (depthB + depthA * Math.cos(angleRad)) / sin);
}

/**
 * A sink at this distance from a corner triggers the single-sided
 * corner-cabinet rule (sink wall gets a blank panel filler;
 * perpendicular wall gets a 36" single-sided corner cabinet). 54"
 * matches the corner-reach buffer used elsewhere — beyond this, a
 * regular cabinet run + 54"-buffer is enough.
 */
const SINK_NEAR_CORNER = 54 * INCHES_TO_METERS;

/**
 * A peninsula's virtual wall is INSET from the peninsula's outer face by the
 * 1" back-panel thickness (`VIRTUAL_WALL_INSET`, virtualPeninsulaWall.js), so
 * an arm that physically dies into a real wall leaves its end that far from the
 * real room corner. Endpoints that close still name the same corner when either
 * wall is virtual — the corner POINT is then the two wall LINES' intersection,
 * which is where the two cabinet RUNS actually meet.
 */
const VIRTUAL_END_EPS = 1.5 * INCHES_TO_METERS;

/** Intersection of the infinite lines (pA, dA) and (pB, dB); null if parallel. */
function lineIntersection(pA, dA, pB, dB) {
  const denom = dA.x * dB.z - dA.z * dB.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((pB.x - pA.x) * dB.z - (pB.z - pA.z) * dB.x) / denom;
  return { x: pA.x + t * dA.x, z: pA.z + t * dA.z };
}

/**
 * Junction record for a wall meeting a corner at `point`: where the point falls
 * on the wall axis, plus the direction the run extends from there. Identical to
 * `wallEndJunction` whenever the point IS that wall end — which is every corner
 * except an inset virtual arm's.
 */
function junctionAtPoint(wall, point, end) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const param = worldToWallParam(wall, point);
  return {
    param: param == null ? (end === 'left' ? 0 : axes.wallLen) : param,
    dirSign: end === 'left' ? 1 : -1,
    point,
  };
}

/**
 * Find the corners (shared endpoints) between walls. We anchor on each wall's
 * `bottomLeft` endpoint — in a closed CCW polygon each corner has exactly one
 * wall whose BL is at it, which gives us a canonical wallA.
 *
 * EVERY pair of walls meeting at a vertex is reported, not just the first one
 * found. Three walls share a vertex wherever a run is split there, and stopping
 * at the first match handed the corner to whichever pair the wall array happened
 * to order first — often a non-cabinet wall, which drops the corner entirely
 * (no L cabinet, no blind-corner reservation) and lets both cabinet runs fill
 * straight into the same corner box. Callers pick the pair they can use and
 * take one corner per vertex (`vertexKey`).
 *
 * Returns: [{ point, vertexKey, wallA, wallAEnd, aJunction, wallB, wallBEnd, bJunction }]
 *   wallAEnd is always 'left'.
 *   wallBEnd ∈ { 'left', 'right' } — which end of wallB is at this corner.
 */
function findSharedCorners(walls) {
  const list = walls || [];
  const corners = [];
  const seenPair = new Set();

  const keyOf = (p) =>
    `${Math.round(p.x * 1000)}_${Math.round((p.z ?? 0) * 1000)}`;

  for (const wall of list) {
    const endPt = wall?.alignment?.bottomLeft;
    if (!endPt) continue;
    const axesA = getWallAxes(wall);
    if (!axesA) continue;

    for (const other of list) {
      if (!other || other.id === wall.id) continue;
      const axesB = getWallAxes(other);
      if (!axesB) continue;
      const tol = wall.isVirtual || other.isVirtual ? VIRTUAL_END_EPS : EPS;

      // Nearest end of `other` to this vertex, within tolerance.
      let otherEnd = null;
      let dist = Infinity;
      for (const end of ['left', 'right']) {
        const p = end === 'left' ? axesB.start : axesB.end;
        const d = Math.hypot(p.x - endPt.x, (p.z ?? 0) - (endPt.z ?? 0));
        if (d < tol && d < dist) {
          dist = d;
          otherEnd = end;
        }
      }
      if (!otherEnd) continue;

      const pairKey = [wall.id, other.id].sort().join('|') + '@' + keyOf(endPt);
      if (seenPair.has(pairKey)) continue;
      seenPair.add(pairKey);

      // Coincident ends share the vertex outright; an inset virtual arm meets
      // the other run one back-panel thickness off it, on its own wall line.
      const point =
        dist < EPS
          ? endPt
          : lineIntersection(
              axesA.start,
              axesA.wallDir,
              axesB.start,
              axesB.wallDir
            ) || endPt;

      corners.push({
        point,
        vertexKey: keyOf(endPt),
        wallA: wall,
        wallAEnd: 'left',
        aJunction: junctionAtPoint(wall, point, 'left'),
        wallB: other,
        wallBEnd: otherEnd,
        bJunction: junctionAtPoint(other, point, otherEnd),
      });
    }
  }

  return corners;
}

// A run END lands on another wall's face within this perpendicular
// distance to count as meeting it. Peninsula arms terminate essentially
// ON the wall (0–1" back-panel inset), so a tight tolerance is enough
// and avoids pairing walls that merely pass near each other.
const RUN_END_PERP_TOL = 6 * INCHES_TO_METERS;

/**
 * Corners the wall-endpoint model misses: a cabinet RUN whose END
 * (a wall end that ISN'T a shared corner — typically a peninsula arm's
 * tip) lands perpendicular on ANOTHER wall's mid-span. As a WALL that's
 * a T-junction, but as a COUNTER RUN it's an ordinary L: the run ends
 * against whatever sits on the other wall at that point (an appliance,
 * or that wall's own run), which `classifyCorner` then resolves exactly
 * like a shared-endpoint corner.
 *
 * The landing wall's junction carries `dirSign: null` — its run
 * direction (which side of the mid-span point the counter continues on)
 * is resolved in `classifyCorner`, where appliances/openings are known.
 *
 * Pure geometry, like `findSharedCorners`.
 */
function findRunEndCorners(walls) {
  const list = walls || [];
  const keyOf = (p) =>
    `${Math.round(p.x * 1000)}_${Math.round((p.z ?? 0) * 1000)}`;

  // Count endpoint coincidences so we can skip ends already owned by a
  // shared corner (a coincident endpoint appears ≥2×).
  const endpointCount = new Map();
  for (const w of list) {
    for (const e of ['bottomLeft', 'bottomRight']) {
      const p = w?.alignment?.[e];
      if (p)
        endpointCount.set(keyOf(p), (endpointCount.get(keyOf(p)) || 0) + 1);
    }
  }

  const out = [];
  const seenPair = new Set();
  for (const wall of list) {
    const axes = getWallAxes(wall);
    if (!axes) continue;
    for (const end of ['left', 'right']) {
      const P = end === 'left' ? axes.start : axes.end;
      if (!P) continue;
      if ((endpointCount.get(keyOf(P)) || 0) > 1) continue; // shared endpoint
      const runDir =
        end === 'left'
          ? axes.wallDir
          : { x: -axes.wallDir.x, z: -axes.wallDir.z };

      let best = null;
      for (const other of list) {
        if (!other || other.id === wall.id) continue;
        const oAxes = getWallAxes(other);
        if (!oAxes) continue;
        const cos = runDir.x * oAxes.wallDir.x + runDir.z * oAxes.wallDir.z;
        if (!isSquareCornerCos(cos)) continue; // not ~perpendicular
        const t = worldToWallParam(other, P);
        if (t == null) continue;
        // Must land well INSIDE the span — a landing near an endpoint is
        // (or should be) a shared corner, not a mid-span run-end.
        if (t < MIN_CABINET_WIDTH || t > oAxes.wallLen - MIN_CABINET_WIDTH)
          continue;
        const proj = {
          x: oAxes.start.x + t * oAxes.wallDir.x,
          z: oAxes.start.z + t * oAxes.wallDir.z,
        };
        const perpDist = Math.hypot(P.x - proj.x, (P.z ?? 0) - proj.z);
        if (perpDist > RUN_END_PERP_TOL) continue;
        if (!best || perpDist < best.perpDist)
          best = { other, t, proj, perpDist };
      }
      if (!best) continue;

      const pairKey =
        [wall.id, best.other.id].sort().join('|') + '@' + keyOf(P);
      if (seenPair.has(pairKey)) continue;
      seenPair.add(pairKey);

      out.push({
        point: P,
        wallA: wall,
        wallAEnd: end,
        aJunction: wallEndJunction(wall, end),
        wallB: best.other,
        wallBEnd: 'mid', // mid-span landing, not a wall end
        bJunction: { param: best.t, dirSign: null, point: best.proj },
        isRunEnd: true,
      });
    }
  }
  return out;
}

/**
 * A "junction" is where a cabinet RUN meets the corner: a point along
 * the wall axis (`param`, meters from `bottomLeft`) plus the direction
 * the run extends from there (`dirSign` = +1 toward `bottomRight`, −1
 * toward `bottomLeft`). A wall END is the special case param ∈ {0,
 * wallLen}; a peninsula arm landing on another wall's mid-span is the
 * general case. Analysing by junction rather than by end-label lets one
 * code path serve wall-end L/U corners, both-`bottomRight` arm joins,
 * and arm↔mid-span junctions uniformly.
 *
 * @returns {{param, dirSign, point} | null}
 */
function wallEndJunction(wall, end) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  return end === 'left'
    ? { param: 0, dirSign: 1, point: axes.start }
    : { param: axes.wallLen, dirSign: -1, point: axes.end };
}

/**
 * Signed gap (meters) from a junction to a placement's body, measured
 * ALONG the run direction. 0 when the placement straddles the junction
 * param; `Infinity` when the placement lies entirely behind the
 * junction (opposite the run direction — it belongs to the other side
 * and isn't part of this run).
 */
function gapAlongRun(range, param, dirSign) {
  if (dirSign > 0) {
    if (range.end <= param) return Infinity; // behind
    if (range.start <= param) return 0; // straddles
    return range.start - param; // ahead
  }
  if (range.start >= param) return Infinity; // behind
  if (range.end >= param) return 0; // straddles
  return param - range.end; // ahead
}

/**
 * For one wall + a run junction on it (see `wallEndJunction`), return:
 *   - applianceAtCorner: the first placement whose body reaches within
 *     one cabinet depth of the junction along the run (or null).
 *   - sinkNearCorner: the first sink within the wider 54" radius.
 *   - freeRunFromCorner: meters of unblocked wall from the junction
 *     along the run before hitting a blocker (appliance OR opening).
 *   - cornerSegmentLen: full length of the run's containing segment
 *     (openings only; appliances don't split it).
 */
function analyzeWallAt(wall, junction, placements, openings) {
  const axes = getWallAxes(wall);
  if (!axes || !junction)
    return { applianceAtCorner: null, freeRunFromCorner: 0 };
  const { param, dirSign } = junction;

  // An appliance "at the corner" includes any appliance close enough
  // that its body would overlap a perpendicular-wall cabinet at the
  // same corner. The perpendicular cabinet projects `cabinetDepth`
  // (24") into the room from its wall, so any appliance whose body
  // reaches within `cabinetDepth` of the junction physically overlaps
  // that cabinet's footprint and effectively claims the corner.
  // (Without this proximity check the corner state would fall to
  // 'wallB-only', the perpendicular wall's fill would extend to the
  // corner, and the close-but-not-at-corner appliance would collide
  // with it.)
  //
  // The radius is the CABINET depth, and that is the right question rather than
  // a convenient constant: the reservation this state drives protects the strip
  // the perpendicular wall's cabinets occupy, which is exactly 24" deep. An
  // appliance whose near edge is further out than that cannot reach the strip,
  // so there is nothing on that wall to keep clear of it.
  //
  // Widening it to the appliance's own depth was tried and REVERTED
  // (2026-09-11): it admits appliances 24-30" out, which by that geometry
  // should reserve nothing, and then hands each the full `depth + 30"` reach —
  // 29 extra `appliance-on-*` corners on one room, starving the fill and
  // leaving full-depth blank panels where bays belonged. It was chasing a
  // different failure: a fridge sitting 1" PAST the strip stands in front of a
  // drawer's face without overlapping its body, so no radius on a body test can
  // see it. That wants a post-fill check on what ended up in front of a face —
  // see the follow-up issue.
  //
  // The test is strict: an appliance whose near edge sits EXACTLY one depth
  // out touches the strip's edge and reaches none of it. That is not a rare
  // case but the common one — slot enumeration seats appliances flush against
  // the 24" corner reservation, so a range or fridge at precisely 24" is what
  // most corner-adjacent placements look like, and a bare `<` let float noise
  // (0.6095999999999995 < 0.6096) claim the corner for them. Each such claim
  // reserved depth + 30" of clearance on the perpendicular wall and left a
  // full-depth blank panel where the first bay belonged. The scorer and the
  // run engine ask the same question through `cornerProximity.claimsCorner`.
  let applianceAtCorner = null;
  for (const p of placements || []) {
    if (claimsCorner(gapAlongRun(p.range, param, dirSign))) {
      applianceAtCorner = p;
      break;
    }
  }

  // Sink within the wider 54" radius of the corner — drives the
  // single-sided-corner-cabinet rule, even when the sink isn't tight
  // enough to count as `applianceAtCorner` (24" radius).
  let sinkNearCorner = null;
  for (const p of placements || []) {
    if (p.applianceType !== 'sink') continue;
    const distFromCorner = gapAlongRun(p.range, param, dirSign);
    if (distFromCorner < SINK_NEAR_CORNER) {
      sinkNearCorner = { placement: p, distFromCorner };
      break;
    }
  }

  const wallOpenings = (openings || []).filter(
    (o) => o.parentWallIds?.[0] === wall.id
  );
  const availableSegments = getAvailableWallSegments(wall, wallOpenings);
  const applianceRanges = (placements || []).map((p) => p.range);

  // The run's containing segment: the available (opening-bounded)
  // segment that spans the junction param. Appliances are part of the
  // cabinet run visually (the run goes around them), so when we're
  // choosing between wallA-only and wallB-only fallback we want to
  // compare total wall continuity, not just bare-cabinet space. A 200in
  // segment containing fridge + cabinets + sink + dishwasher + stove is
  // a much "longer run" than a 31in cabinet-only segment hemmed in by a
  // door, even though both have similar freeRunFromCorner.
  let freeRunFromCorner = 0;
  let cornerSegmentLen = 0;
  // What ENDS the free run: an appliance body, or the segment's own end (an
  // opening, the wall). A corner that refuses this leg has to know, because an
  // appliance is a body standing in the room and an opening is not
  // (`refusable`).
  let freeRunStoppedBy = null;
  const seg = availableSegments.find(
    (s) => s.start - EPS <= param && param <= s.end + EPS
  );
  if (seg) {
    cornerSegmentLen = seg.end - seg.start;
    if (dirSign > 0) {
      const blocking = applianceRanges
        .filter((r) => r.start >= param - EPS && r.start < seg.end)
        .sort((a, b) => a.start - b.start);
      const firstBlock = blocking.length ? blocking[0].start : seg.end;
      freeRunStoppedBy = blocking.length ? 'appliance' : 'segment-end';
      freeRunFromCorner = Math.max(0, firstBlock - param);
    } else {
      const blocking = applianceRanges
        .filter((r) => r.end > seg.start && r.end <= param + EPS)
        .sort((a, b) => b.end - a.end);
      const firstBlock = blocking.length ? blocking[0].end : seg.start;
      freeRunStoppedBy = blocking.length ? 'appliance' : 'segment-end';
      freeRunFromCorner = Math.max(0, param - firstBlock);
    }
  }
  // Where the wall's cabinetry resumes PAST the free run — the next segment
  // along, across the opening that ended this one. `nearestRunDist` cannot
  // answer this: it is 0 whenever the junction sits in a segment of its own.
  let nextRunStart = Infinity;
  for (const s of availableSegments) {
    const d = dirSign > 0 ? s.start - param : param - s.end;
    if (d > freeRunFromCorner + EPS && d < nextRunStart) nextRunStart = d;
  }

  // Distance (meters) from the junction to the NEAREST cabinetable run on
  // this wall, measured along the run direction — 0 if the junction sits
  // inside a segment, else the gap to the closest segment ahead. Unlike
  // `freeRunFromCorner` (which is 0 when an opening blocks the exact
  // corner), this stays small when the wall's run merely starts a few
  // inches away — e.g. a low-sill window clipping the last 3" of the
  // corner. Lets the corner decider tell "wall genuinely bare at the
  // corner" from "wall's run boxes a perpendicular corner cabinet."
  let nearestRunDist = Infinity;
  for (const s of availableSegments) {
    if (param >= s.start - EPS && param <= s.end + EPS) {
      nearestRunDist = 0;
      break;
    }
    const d = dirSign > 0 ? s.start - param : param - s.end;
    if (d > 0 && d < nearestRunDist) nearestRunDist = d;
  }

  return {
    applianceAtCorner,
    sinkNearCorner,
    freeRunFromCorner,
    freeRunStoppedBy,
    nextRunStart,
    cornerSegmentLen,
    nearestRunDist,
  };
}

/**
 * Back-compat wrapper: analyse a wall's corner END ('left' = at
 * `bottomLeft`, 'right' = at `bottomRight`) by building the equivalent
 * junction. Existing callers (`classifyCorner`, `cornerRun` scoring,
 * the upper-L emitter) keep their end-label API.
 */
function analyzeWallEnd(wall, end, placements, openings) {
  return analyzeWallAt(wall, wallEndJunction(wall, end), placements, openings);
}

/**
 * A mid-span run-end junction (`dirSign === null`) doesn't know yet
 * which way the landed-on wall's counter continues. Resolve it by
 * picking the side with the longer free run — that's the direction the
 * counter actually extends. Junctions that already carry a `dirSign`
 * (every wall-end junction) pass through untouched.
 *
 * When neither side has free run AT the junction — an arm landing inside a
 * doorway's clearance buffer reads zero both ways — the tie goes to the side
 * whose run begins NEARER. Breaking it toward +1 regardless pointed the
 * corner reservation into the doorway, where nothing would ever be built, and
 * left the side that actually carries the run unreserved: the fill then ran
 * up to the buffer edge, through the arm's corner cabinet. Measured
 * 2026-09-12 over the kitchen corpus: 56 of the 90 plain one-run corners with
 * a blocked front had their junction resolved this way.
 */
function resolveRunDirection(wall, junction, placements, openings) {
  if (!junction || junction.dirSign != null) return junction;
  const plus = analyzeWallAt(
    wall,
    { ...junction, dirSign: 1 },
    placements,
    openings
  );
  const minus = analyzeWallAt(
    wall,
    { ...junction, dirSign: -1 },
    placements,
    openings
  );
  let dirSign;
  if (plus.freeRunFromCorner !== minus.freeRunFromCorner) {
    dirSign = plus.freeRunFromCorner > minus.freeRunFromCorner ? 1 : -1;
  } else {
    dirSign = plus.nearestRunDist <= minus.nearestRunDist ? 1 : -1;
  }
  return { ...junction, dirSign };
}

/**
 * The tolerance a corner between these two walls is measured at.
 *
 * `findSharedCorners` MATCHES a virtual arm's end to a real wall's end at
 * `VIRTUAL_END_EPS`, because a peninsula arm's back panel sits against the wall
 * it meets and its own wall line is therefore one panel thickness (1") short of
 * the vertex. Every later question about that corner has to be asked at the
 * same tolerance, or the pairing admits a corner the rest of the pipeline then
 * refuses as a mid-span landing.
 */
function cornerPairTolerance(wallA, wallB) {
  return wallA?.isVirtual || wallB?.isVirtual ? VIRTUAL_END_EPS : EPS;
}

/**
 * True when a junction sits at a wall END (param ≈ 0 or ≈ wallLen).
 *
 * `tol` must be the tolerance the CORNER was matched at (`cornerPairTolerance`),
 * not the bare geometric epsilon. At the default 0.39" an inset peninsula arm's
 * corner — paired at 1.5", landing 1" in — reads as a mid-span T-landing, and
 * the states that seat a unit are all gated on this. Measured 2026-09-12 across
 * the kitchen corpus: 241 of 319 blanked blind corner boxes were exactly this,
 * every one a real wall meeting a virtual arm 1"-2" from the end it was paired
 * at. The other 78 are genuine mid-span landings and still fail here.
 */
function isWallEndJunction(wall, junction, tol = EPS) {
  const axes = getWallAxes(wall);
  if (!axes || !junction) return false;
  return junction.param <= tol || junction.param >= axes.wallLen - tol;
}

/**
 * Classify a single corner. wallA is always at end='left'; wallB's
 * end is taken from corner.wallBEnd.
 *
 * Rule (per user spec): for two walls meeting at a corner, if BOTH
 * walls have cabinet runs approaching the corner, place a corner
 * cabinet. If only ONE wall has a run approaching the corner, let
 * that run continue to the corner; the other wall has nothing there.
 *
 * A wall has a "cabinet run approaching the corner" iff there's
 * enough open wall at the corner end for at least one cabinet
 * (≥ MIN_CABINET_WIDTH after subtracting doors/openings/appliances).
 */
/**
 * The vertex is not a corner: the long wall runs to it plainly, and the leg
 * that cannot hold a cabinet is REFUSED — reserved whole, so the fill stands
 * nothing in it and the persisted run carries it as floor
 * (`materializeCandidate`'s `refusedLegRanges`). Not `blindBox`: nothing of
 * the refused leg's own will stand across the corner box, which is the only
 * thing that flag means.
 */
/**
 * May this short leg be refused outright — the other wall's run reaching the
 * vertex as an ordinary cabinet?
 *
 * Two conditions. (1) Nothing worth building is left beside the corner body:
 * `legHoldsAtCorner`. (2) The cabinet that will reach the vertex must still
 * have FLOOR IN FRONT OF IT. Refusing the leg empties the wall, but not what
 * stands past it: on `0D4BA2A8`'s Room 1 a range starts 27" along the refused
 * wall and reaches 25" into the room, straight across the front of the last
 * foot of a 36" cabinet — no overlap, and no way to open it
 * (`cornerBlindBox.nightly`, 10 layouts). Measuring the 24" corner box ALONG the
 * wall misses that entirely; the front zone is what matters, so the first body
 * on the refused wall has to clear a cabinet depth plus the standing room in
 * front of it (`APPLIANCE_FRONT_CLEARANCE`, 21" — 45" in all).
 *
 * Where it does not clear, the box is genuinely blind and the single-sided
 * unit is still the right piece: its door opens on the part you can reach.
 */
function refusable(side) {
  const freeRun = side?.freeRunFromCorner || 0;
  if (legHoldsAtCorner(freeRun)) return false;
  const applianceAhead =
    side?.freeRunStoppedBy === 'appliance' ? freeRun : Infinity;
  const firstBody = Math.min(applianceAhead, side?.nextRunStart ?? Infinity);
  return firstBody >= CORNER_CLAIM + APPLIANCE_FRONT_CLEARANCE;
}

function refuseLeg(corner, state, refusedWall, refusedJunction, side, rest) {
  return {
    ...corner,
    state,
    blindBox: false,
    refusedLeg: {
      wallId: refusedWall.id,
      junction: refusedJunction,
      length: side.freeRunFromCorner || 0,
    },
    ...rest,
  };
}

function classifyCorner(
  corner,
  placementsByWall,
  openings,
  units = DEFAULT_CORNER_UNITS
) {
  const { wallA, wallB, wallBEnd } = corner;
  // A corner is planned only as a unit the line SELLS: an L at a size it makes
  // on both legs, a single-sided unit on a leg long enough for its shortest
  // blind base. Asking a fixed 36" instead promised Venus — blind bases from
  // 39" — a unit on a 38.5" leg that the build could then not make, and the
  // corner shipped as a bare hole in the counter (Golden West, 2026-10-19).
  const lSet = units?.lSet || DEFAULT_CORNER_L_SET;
  const blindLeg = shortestCornerBlindM(
    units?.blindSet || DEFAULT_CORNER_BLIND_SET
  );
  const aPlacements = placementsByWall.get(wallA.id) || [];
  const bPlacements = placementsByWall.get(wallB.id) || [];
  const aJunction = resolveRunDirection(
    wallA,
    corner.aJunction || wallEndJunction(wallA, 'left'),
    aPlacements,
    openings
  );
  const bJunction = resolveRunDirection(
    wallB,
    corner.bJunction || wallEndJunction(wallB, wallBEnd),
    bPlacements,
    openings
  );
  const a = analyzeWallAt(wallA, aJunction, aPlacements, openings);
  const b = analyzeWallAt(wallB, bJunction, bPlacements, openings);

  // L-shaped corner cabinets and single-sided corner cabinets both
  // assume the wall pair is orthogonal. At an obtuse / acute joint
  // (e.g. a diagonal wall meeting a vertical wall at ~135°) the L
  // footprint can't actually fit the corner geometry, so we skip
  // those states entirely and fall through to the per-wall cases.
  // Enrich the corner with the resolved junctions + explicit wallAEnd
  // so the angle helpers (which read run directions off the junctions)
  // and every returned classification carry them. For shared wall-end
  // corners the junctions reduce to the wall ends, so this is identical
  // to the pre-junction behaviour.
  const cornerJ = {
    ...corner,
    aJunction,
    bJunction,
    wallAEnd: corner.wallAEnd || 'left',
  };
  const perpendicular = isPerpendicularCorner(cornerJ);
  // Compute angle data once and attach to every classification — the
  // materializer needs it for both the dedicated 'non-right-angle'
  // state (no appliance at corner) and to angle-correct the
  // appliance-on-A / appliance-on-B reach blockers.
  const interiorAngle = perpendicular ? Math.PI / 2 : interiorAngleAt(cornerJ);
  const stopDistance = perpendicular
    ? null
    : nonRightAngleStopDistance(interiorAngle);
  const angleData = {
    perpendicular,
    interiorAngle,
    stopDistance,
    aJunction,
    bJunction,
    wallAEnd: cornerJ.wallAEnd,
  };

  // An appliance NEAR the corner leaves the stretch between it and the vertex
  // to be filled, and that stretch is blind whenever the other wall's run
  // actually OCCUPIES the corner box. Marking it `blindBox` here was tried and
  // reverted (2026-09-11): the predicate available at classify time is "does
  // the other wall have free run", which is not the same question and fires far
  // too widely — measured on Brown's kitchen it blanked a usable bay in every
  // layout, costing a real cabinet each time, to fix one blocked drawer in one
  // layout of another room.
  //
  // Whether the box is blind depends on what the FILL ends up putting there,
  // which is not known until after it runs. See the follow-up issue; the honest
  // shape is a post-fill pass that finds a fully-blocked front and converts it,
  // not a prediction made before the fill.
  const APPLIANCE_CORNER_BLIND_BOX = false;
  if (a.applianceAtCorner) {
    return {
      ...corner,
      state: 'appliance-on-A',
      // DISABLED pending a better predicate — see below.
      blindBox: APPLIANCE_CORNER_BLIND_BOX,
      a,
      b,
      ...angleData,
    };
  }
  if (b.applianceAtCorner) {
    return {
      ...corner,
      state: 'appliance-on-B',
      blindBox: APPLIANCE_CORNER_BLIND_BOX,
      a,
      b,
      ...angleData,
    };
  }

  // Non-perpendicular corners (obtuse / acute joints): the L corner
  // cabinet and single-sided corner cabinet both assume orthogonal
  // walls, and the wallA-only / wallB-only one-cabinet-depth corner
  // reservation is geometrically wrong for these angles. Replace with a
  // single state that carries the interior angle and the touching-
  // point stop distance computed from `depth / tan(θ/2)`.
  if (!perpendicular) {
    return {
      ...corner,
      state: 'non-right-angle',
      a,
      b,
      ...angleData,
    };
  }

  const aHasRun = a.freeRunFromCorner >= MIN_CABINET_WIDTH;
  const bHasRun = b.freeRunFromCorner >= MIN_CABINET_WIDTH;

  // The L corner cabinet and single-sided corner cabinet both seat their
  // body at a wall END. At a mid-span run-end junction (a peninsula arm
  // landing on another wall's face) there's no end to seat them against,
  // so skip those states and fall through to the one-depth blind-corner
  // reservation (wallA-only / wallB-only), which is junction-relative.
  const endTol = cornerPairTolerance(wallA, wallB);
  const bothWallEnds =
    isWallEndJunction(wallA, aJunction, endTol) &&
    isWallEndJunction(wallB, bJunction, endTol);

  if (aHasRun && bHasRun) {
    // Both walls have cabinet runs at the corner — need a corner
    // cabinet to resolve the blind-corner overlap, but only if the
    // joint is perpendicular (the L cabinet's footprint depends on
    // orthogonal axes).
    const lIn =
      perpendicular && bothWallEnds
        ? pickCornerLIn(lSet, a.freeRunFromCorner, b.freeRunFromCorner)
        : 0;
    if (lIn > 0) {
      return {
        ...corner,
        state: 'corner-cabinet',
        // The L's leg on both walls, from the vertex — the size the line sells.
        lLength: lIn * INCHES_TO_METERS,
        a,
        b,
        ...angleData,
      };
    }
    // No L the line sells fits (one wall is shorter than its smallest L).
    // If the long wall can fit the line's shortest single-sided corner
    // cabinet (36", or 39" in Venus), place it
    // there and cover the short wall's corner-side gap with a blank
    // panel — same treatment as the sink-near-corner case above, but
    // triggered by run length alone (no sink required). This keeps
    // the corner from looking unfinished when one wall has limited
    // run because of an appliance / opening 30-something inches off
    // the corner.
    // The single-sided fallback only makes sense when:
    //   1. The SHORT-run wall has a placement whose corner-near edge
    //      sits within freeRunFromCorner of the corner — that's what
    //      the corner-side blank panel butts against. Without it,
    //      we'd emit a stranded perpendicular cabinet sticking into
    //      the corner with bare wall on the adjoining side.
    //   2. The CABINET wall (the one hosting the 36" single-sided
    //      cabinet) also has at least one neighboring placement, so
    //      the single-sided piece isn't a marooned 36" cabinet sitting
    //      alone on a wall that's otherwise just door clearance. We
    //      check the OPPOSITE end of the cabinet wall (the corner-end
    //      itself is consumed by the single-sided piece) — that gives
    //      a real "rest of the cabinet run" to join up with.
    // If either anchor fails, fall through to wallA-only / wallB-only
    // and skip the single-sided emission entirely.
    //
    // The blind unit is tried on RUN LENGTH, not on the presence of appliances.
    // These two tests used to also require a placement anchored at the short
    // wall's corner end and any placement at all on the long wall — guards
    // against "a stranded cabinet sticking into the corner with bare wall
    // beside it" and "a marooned 36" cabinet alone on a wall that is otherwise
    // door clearance".
    //
    // `placementsByWall` holds APPLIANCES only — sink, range, fridge,
    // dishwasher. Cabinets are filled later and never appear in it, so a wall
    // carrying ten feet of cabinetry and no appliance read as empty and its
    // corner was refused a blind unit on that basis. The guards were affordable
    // while the asymmetric L caught everything that fell through; with that
    // branch gone they are what stands between a corner and the unit it should
    // get, and only ~26 of 173 displaced corners were reaching this rung.
    //
    // What actually matters is already tested above: the long wall has a full
    // 36" of free run, and the short wall does not. A unit that would still sit
    // badly is rejected downstream on geometry rather than on inventory —
    // `isInClearanceZone` and `overlapsCorridor` both gate the emission.
    // One leg long, the other short. A single-sided unit used to go on the
    // long one — its body standing 24" along the SHORT leg, which then had
    // whatever the openings left it: on FB5B0707 a 31" leg, so 3", so a filler
    // scribed to a cabinet on one side and a doorway's casing margin on the
    // other. `legHoldsAtCorner` asks the short leg the question nobody asked:
    // is a real cabinet left once the body has taken its depth? When the
    // answer is no, this is NOT A CORNER. The long wall's run reaches the
    // vertex as an ordinary cabinet — its side against the perpendicular wall,
    // nothing exposed — and the short leg is refused entirely rather than
    // packed with what fits (user rulings, 2026-09-26; stage 2 of the plan).
    //
    // WHICH leg is long is asked against the fixed 36", line or no line.
    // Refusing builds no corner unit, so the line has no say in it; the line's
    // shortest blind base (`blindLeg`) only decides whether the single-sided
    // unit can be made. Asking `blindLeg` for both (#2642) sent a Venus long
    // leg of 36-39" beside a refusable short leg to the blind-box fallback
    // below — a dead 24" box where the run used to reach the vertex.
    if (perpendicular && bothWallEnds) {
      const aLong = a.freeRunFromCorner >= SINGLE_SIDED_CORNER_LENGTH;
      const bLong = b.freeRunFromCorner >= SINGLE_SIDED_CORNER_LENGTH;
      if (aLong && !bLong) {
        if (refusable(b)) {
          return refuseLeg(corner, 'wallA-only', wallB, bJunction, b, {
            a,
            b,
            ...angleData,
          });
        }
        if (legTakes(a.freeRunFromCorner, blindLeg)) {
          return { ...corner, state: 'single-sided-on-A', a, b, ...angleData };
        }
      }
      if (bLong && !aLong) {
        if (refusable(a)) {
          return refuseLeg(corner, 'wallB-only', wallA, aJunction, a, {
            a,
            b,
            ...angleData,
          });
        }
        if (legTakes(b.freeRunFromCorner, blindLeg)) {
          return { ...corner, state: 'single-sided-on-B', a, b, ...angleData };
        }
      }
      // BOTH legs short. A leg that passes the same two tests is refused here
      // too: nothing of it will stand across the box's front, so the box is
      // not blind and the other wall's run reaches the vertex as an ordinary
      // run end, filled with what the line sells. Asking only when one leg
      // was long left a contractor's L+peninsula (29.2" to a doorway, 24" to the range)
      // a blank box — and an open hole in the counter, since the engine spans
      // an empty box only at a corner inside one run (2026-10-20). Only a leg
      // whose first body stands across the box's front leaves it blind (the
      // fallback below). When both legs could be refused, the wall the
      // fallback would extend — the longer corner segment — extends, so the
      // only change is the box.
      if (!aLong && !bLong) {
        const aRefusable = refusable(a);
        const bRefusable = refusable(b);
        if (aRefusable || bRefusable) {
          const extendA =
            aRefusable && bRefusable
              ? a.cornerSegmentLen >= b.cornerSegmentLen
              : bRefusable;
          return extendA
            ? refuseLeg(corner, 'wallA-only', wallB, bJunction, b, {
                a,
                b,
                ...angleData,
              })
            : refuseLeg(corner, 'wallB-only', wallA, aJunction, a, {
                a,
                b,
                ...angleData,
              });
        }
      }
    }
    // Neither wall has 36" of run and neither leg can be refused (or a
    // non-perpendicular joint, or a long leg under the line's shortest blind
    // base beside a short leg that cannot be refused). Pick
    // the wall whose corner-end SEGMENT (= contiguous wall length
    // bounded only by openings, not by appliances) is longer — that
    // wall is part of a longer cabinet+appliance run, and extending
    // it to the corner avoids cutting off a long stove/sink/cabinet
    // line in favor of a short cabinet-only stub on the other wall.
    //
    // `blindBox` marks this as a TRUE blind corner: BOTH walls carry runs, so
    // whichever one reaches the vertex has the other's body across the front of
    // its corner box. The same two states are reached further down when the
    // other wall genuinely has nothing at the corner — there the box is open
    // and an ordinary cabinet is fine — so the materializer needs to tell them
    // apart. Without it the run-bearing wall packs a plain drawer base into a
    // 24"x24" box you cannot open (user-reported 2026-09-11).
    return a.cornerSegmentLen >= b.cornerSegmentLen
      ? { ...corner, state: 'wallA-only', blindBox: true, a, b, ...angleData }
      : { ...corner, state: 'wallB-only', blindBox: true, a, b, ...angleData };
  }

  // Only one wall has free run AT the corner, but the OTHER wall's run
  // begins within a corner-box of the corner (e.g. its exact corner is
  // clipped by a low-sill window, yet cabinets resume a few inches in).
  // Running the first wall's cabinet straight to the corner would box it
  // against that perpendicular run. Instead seat a single-sided corner
  // cabinet on the wall that has the run — a blind-corner unit whose
  // door opens on its accessible (non-corner) portion, the corner slot
  // combined into it — and reserve the corner box on the other wall so
  // its run clears the body. Requires ≥36" of run to host the piece.
  const CORNER_BOX = KITCHEN_CABINET_DIMENSIONS.depth;
  if (perpendicular && bothWallEnds) {
    if (
      aHasRun &&
      !bHasRun &&
      legTakes(a.freeRunFromCorner, blindLeg) &&
      b.nearestRunDist < CORNER_BOX
    ) {
      return { ...corner, state: 'single-sided-on-A', a, b, ...angleData };
    }
    if (
      bHasRun &&
      !aHasRun &&
      legTakes(b.freeRunFromCorner, blindLeg) &&
      a.nearestRunDist < CORNER_BOX
    ) {
      return { ...corner, state: 'single-sided-on-B', a, b, ...angleData };
    }
  }

  // Only one wall (or neither) has a run at the corner. The wall with
  // a run continues to the corner; the other reserves one cabinet depth.
  //
  // Whether that corner box is BLIND is the question the fill will answer, so
  // it has to be asked the way the fill answers it. `freeRunFromCorner` is
  // measured from the junction and reads zero when a doorway's buffer sits on
  // it — but the fill starts its first body at the far edge of the 24"
  // reservation, and a wall whose cabinetable segment begins anywhere inside
  // that box gets a body flush against the reservation edge, standing across
  // the front of whatever the run wall put in its corner box. The classifier
  // asked "is there free run at the junction", the fill did "start one depth
  // in and fill", and the two disagreed by exactly the reservation width: 63
  // of the 156 blocked cabinet fronts over the kitchen corpus (2026-09-12).
  // `nearestRunDist < CORNER_BOX` is the same predicate the single-sided rung
  // above already uses for "the other wall's run begins within a corner-box".
  if (aHasRun) {
    return {
      ...corner,
      state: 'wallA-only',
      blindBox: b.nearestRunDist < CORNER_BOX,
      a,
      b,
      ...angleData,
    };
  }
  if (bHasRun) {
    return {
      ...corner,
      state: 'wallB-only',
      blindBox: a.nearestRunDist < CORNER_BOX,
      a,
      b,
      ...angleData,
    };
  }
  return { ...corner, state: 'none', a, b, ...angleData };
}

/**
 * @param {Array} walls
 * @param {Map<string, Array>} placementsByWall - wallId → appliance placements.
 * @param {Array} openings - doors + openings (anything blocking walls).
 * @returns {Array<{point, wallA, wallAEnd, wallB, wallBEnd, state, a, b}>}
 *   Shared wall-end corners (`findSharedCorners`, wallAEnd='left') PLUS
 *   mid-span run-end corners (`findRunEndCorners`, wallBEnd='mid').
 */
function detectCornerStates(walls, placementsByWall, openings, units) {
  const corners = [...findSharedCorners(walls), ...findRunEndCorners(walls)];
  return corners.map((c) =>
    classifyCorner(c, placementsByWall, openings, units)
  );
}

/**
 * A "corner zone" is the set of (wallId, end) pairs that all sit in
 * the same physical area inside the room. Two corners belong to the
 * same zone if:
 *   - they're directly shared (`findSharedCorners`), OR
 *   - they're connected through a chain of bridge walls each shorter
 *     than `bridgeMaxLen`. A short bridge wall doesn't separate the
 *     two flanking walls' corners enough for cabinets / appliances on
 *     either side to avoid each other — they reach across the bridge
 *     and overlap.
 *
 * Two appliances claiming corner-ends in the same zone always overlap
 * geometrically and must be rejected at enumeration time. Likewise
 * fill-cabinets at corner-ends in the same zone need to leave room
 * for whatever else lands there.
 *
 * @param {Array} walls
 * @param {Object} [options]
 * @param {number} [options.bridgeMaxLen=1.2192] - Bridge walls shorter
 *   than this are absorbed into the adjacent zones. Default 48"
 *   (twice the 24" cabinet depth — past this, two cabinets on the
 *   flanking walls can't reach each other through the bridge).
 * @returns {Map<string, string>} `${wallId}:${end}` → zoneId
 */
function buildCornerZones(walls, options = {}) {
  const { bridgeMaxLen = 48 * 0.0254 } = options;

  const wallLen = new Map();
  for (const w of walls || []) {
    const axes = getWallAxes(w);
    if (axes) wallLen.set(w.id, axes.wallLen);
  }

  // Union-Find over `(wallId, end)` keys. Direct shared corners union
  // their two endpoints. Short bridge walls additionally union their
  // own two endpoints, daisy-chaining the flanking corners' zones.
  const parent = new Map();
  const find = (k) => {
    if (!parent.has(k)) {
      parent.set(k, k);
      return k;
    }
    const p = parent.get(k);
    if (p === k) return k;
    const root = find(p);
    parent.set(k, root);
    return root;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  for (const c of findSharedCorners(walls || [])) {
    union(`${c.wallA.id}:left`, `${c.wallB.id}:${c.wallBEnd}`);
  }

  for (const [wid, len] of wallLen.entries()) {
    if (len < bridgeMaxLen) {
      union(`${wid}:left`, `${wid}:right`);
    }
  }

  const zones = new Map();
  for (const k of parent.keys()) {
    zones.set(k, find(k));
  }
  return zones;
}

/**
 * Find "virtual corners" between open wall ends.
 *
 * An open wall end is one whose endpoint isn't shared with any other
 * kitchen wall — typical when a wall outside the kitchen perimeter
 * (e.g. a partition between rooms) closes the geometry but isn't
 * tracked here. When two such open ends sit close together (within
 * `virtualMaxLen`, ~36" by default), they form a non-standard corner:
 * the two walls don't meet at a single point, but they're close
 * enough that cabinets on each will physically share space at the
 * notch they enclose. Treating them as a virtual corner lets the
 * materializer allow that intersection rather than rejecting one of
 * the cabinets via an overlap check.
 *
 * Two open ends are paired only if they're on different walls. Each
 * open end can pair with at most one other (the closest) — multiple
 * notches in a row would each form their own virtual corner.
 *
 * @returns {Array<{wallA, wallAEnd, wallB, wallBEnd, distance}>}
 */
function findVirtualCorners(walls, options = {}) {
  const { virtualMaxLen = 36 * 0.0254 } = options;
  if (!walls || walls.length === 0) return [];

  const ends = [];
  for (const wall of walls) {
    const bl = wall?.alignment?.bottomLeft;
    const br = wall?.alignment?.bottomRight;
    if (bl) ends.push({ wall, end: 'left', point: bl });
    if (br) ends.push({ wall, end: 'right', point: br });
  }

  const pointKey = (p) =>
    `${Math.round(p.x * 1000)}_${Math.round((p.z ?? 0) * 1000)}`;
  const pointCount = new Map();
  for (const e of ends) {
    const k = pointKey(e.point);
    pointCount.set(k, (pointCount.get(k) || 0) + 1);
  }
  const open = ends.filter((e) => pointCount.get(pointKey(e.point)) === 1);

  // Pair each open end with its single closest open end on a different
  // wall, provided the distance is under `virtualMaxLen`. Both members
  // of the pair must independently pick each other for it to count —
  // prevents weird three-way ties.
  const closest = new Map();
  for (let i = 0; i < open.length; i++) {
    const a = open[i];
    let bestJ = -1;
    let bestDist = virtualMaxLen + 1;
    for (let j = 0; j < open.length; j++) {
      if (i === j) continue;
      const b = open[j];
      if (a.wall.id === b.wall.id) continue;
      const dx = a.point.x - b.point.x;
      const dz = (a.point.z ?? 0) - (b.point.z ?? 0);
      const d = Math.hypot(dx, dz);
      if (d > virtualMaxLen) continue;
      if (d < bestDist) {
        bestDist = d;
        bestJ = j;
      }
    }
    if (bestJ >= 0) closest.set(i, { j: bestJ, dist: bestDist });
  }

  const out = [];
  const seen = new Set();
  for (const [i, { j, dist }] of closest.entries()) {
    if (seen.has(i) || seen.has(j)) continue;
    if (closest.get(j)?.j !== i) continue;
    seen.add(i);
    seen.add(j);
    out.push({
      wallA: open[i].wall,
      wallAEnd: open[i].end,
      wallB: open[j].wall,
      wallBEnd: open[j].end,
      distance: dist,
    });
  }
  return out;
}

module.exports = {
  VIRTUAL_END_EPS,
  // The one square-or-oblique rule. `segmentGraph.js` asks it of two walls and
  // the run engine (`cabinet-runs/corners.js`) of two run legs.
  SQUARE_CORNER_TOL_DEG,
  isSquareCornerCos,
  detectCornerStates,
  findSharedCorners,
  findRunEndCorners,
  isWallEndJunction,
  cornerPairTolerance,
  classifyCorner,
  analyzeWallEnd,
  analyzeWallAt,
  wallEndJunction,
  buildCornerZones,
  findVirtualCorners,
  interiorAngleAt,
  nonRightAngleStopDistance,
  runStopDistance,
  legStopClearingBody,
  isPerpendicularCorner,
};
